import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

/**
 * EXPLANATION LAYERS (PART E — FINAL LOCKED DIRECTIVE)
 * 
 * Info icon or tooltip explaining:
 * - Feed
 * - Commitments
 * - Groups
 * - Commons
 * - SCUTA
 * - Calendar
 * - Placard
 * 
 * No feature may exist without explanation.
 */

const EXPLANATIONS = {
  feed: {
    title: 'What is the Feed?',
    content: 'The Feed shows activities and updates from your groups and connections. Unlike social media feeds, this is not algorithmic — items appear in chronological order. Nothing is ranked or manipulated to keep you engaged.',
    tip: 'You can customize what appears in your feed from Settings.'
  },
  commitments: {
    title: 'What are Commitments?',
    content: 'Commitments are your RSVPs to events and activities. When you say "I\'ll be there," it appears here. Others can see your commitments only if you choose to share them.',
    tip: 'You control whether your commitments are visible to others in each event.'
  },
  groups: {
    title: 'What are Groups?',
    content: 'Groups are temporary gatherings around shared activities or interests. By default, groups expire after 90 days to prevent accumulating stale communities. Groups are not permanent — for lasting communities, use Commons.',
    tip: 'When a group expires, members are notified. Active groups can be extended.'
  },
  commons: {
    title: 'What is a Commons?',
    content: 'A Commons is a permanent community space managed by stewards. Unlike groups, Commons don\'t expire. They\'re for neighborhoods, organizations, schools, and ongoing communities. Every Commons requires at least two stewards.',
    tip: 'Dual-steward requirement prevents any single person from having unchecked control.'
  },
  scuta: {
    title: 'What is SCUTA?',
    content: 'SCUTA is a timeline instrument. It helps people see time clearly — not as opinion, not as noise, not as engagement — but as sequence, pattern, and consequence. SCUTA presents historical events without judgment or manipulation.',
    tip: 'Public historical timelines are viewable without a LikeKind account.'
  },
  calendar: {
    title: 'What is the Calendar?',
    content: 'The Calendar shows your upcoming events, commitments, and activities. It aggregates events from your groups, Commons, family, and personal schedule. You can export to Google, Apple, or Outlook calendars.',
    tip: 'Events are shown in chronological order. Nothing is hidden or reordered.'
  },
  placard: {
    title: 'What is a Placard?',
    content: 'A Placard is your public-facing identity card on LikeKind. It shows only what you choose: your name (or pseudonym), categories of interest, and a brief bio. Placards are not profiles — there\'s no feed, no follower count, no engagement metrics.',
    tip: 'You control exactly what appears on your placard. Less is often more.'
  },
  myworld: {
    title: 'What is Base Camp?',
    content: 'Base Camp is your private dashboard. It shows your groups, commitments, contacts, calendar, and activities — all in one place. You control what sections are visible to others.',
    tip: 'By default, everything is private. You choose what to share.'
  },
  visibility: {
    title: 'What are Visibility Settings?',
    content: 'Visibility settings control who can see each section of your profile. Options include: Private (only you), Contacts (people you\'ve connected with), Groups (members of your groups), or Public (anyone on LikeKind).',
    tip: 'You can set different visibility for each section of Base Camp.'
  },
  student_notebook: {
    title: 'What is a Student Notebook?',
    content: 'A Student Notebook is NOT a social profile. It\'s a one-way communication channel from teachers to students. Teachers send homework, announcements, and notes. Students can acknowledge but not post. Parents can see everything.',
    tip: 'Student-to-student messaging is disabled by design for safety.'
  },
  parent_mode: {
    title: 'What is Parent Mode?',
    content: 'Parent Mode gives parents visibility into their child\'s LikeKind activity. Children always see when Parent Mode is ON. There are three levels: ON (full visibility), SHARED (both can see), and OFF (reduced oversight for teens 13+).',
    tip: 'Children under 13 require full parent visibility (ON).'
  },
  dual_steward: {
    title: 'What is Dual-Steward?',
    content: 'Every Commons and Institution requires two stewards. Critical actions — deleting the space, removing stewards, changing safety settings — require approval from both. This prevents any single person from having unchecked control.',
    tip: 'You cannot remove the other steward without their approval.'
  },
  intent_cards: {
    title: 'What are Intent Cards?',
    content: 'Intent Cards replace comments and reactions. Instead of typing responses, you choose from meaningful options like "I\'m interested," "I can help," or "I have questions." This encourages action over engagement.',
    tip: 'Intent Cards are designed to create real connections, not empty interactions.'
  }
};

export function InfoTooltip({ topic, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const explanation = EXPLANATIONS[topic];
  
  if (!explanation) {
    console.warn(`No explanation found for topic: ${topic}`);
    return null;
  }

  return (
    <div className={`relative inline-flex ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-1 rounded-full hover:bg-black/5 transition-colors"
        aria-label={`Learn about ${topic}`}
        data-testid={`info-tooltip-${topic}`}
      >
        <Info className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Tooltip */}
          <div 
            className="absolute z-50 w-72 p-4 rounded-xl shadow-lg animate-in fade-in slide-in-from-top-2 duration-200"
            style={{ 
              backgroundColor: 'var(--fy-surface-card)', 
              border: '1px solid var(--fy-border-subtle)',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '8px'
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <h4 className="font-semibold text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                {explanation.title}
              </h4>
              <button onClick={() => setIsOpen(false)} className="p-1 -m-1 rounded-full hover:bg-black/5">
                <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
              </button>
            </div>
            <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
              {explanation.content}
            </p>
            {explanation.tip && (
              <p className="text-xs p-2 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-disabled)' }}>
                <strong>Tip:</strong> {explanation.tip}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function ExplanationCard({ topic, compact = false }) {
  const explanation = EXPLANATIONS[topic];
  
  if (!explanation) return null;

  if (compact) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} />
        <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
          {explanation.content}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
      <h4 className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
        <Info className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
        {explanation.title}
      </h4>
      <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--fy-text-secondary)' }}>
        {explanation.content}
      </p>
      {explanation.tip && (
        <p className="text-xs p-2 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-disabled)' }}>
          <strong>Tip:</strong> {explanation.tip}
        </p>
      )}
    </div>
  );
}

export function SectionHeader({ title, topic, actions }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <h2 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>{title}</h2>
        {topic && <InfoTooltip topic={topic} />}
      </div>
      {actions}
    </div>
  );
}

// Export all explanations for use in other components
export const FEATURE_EXPLANATIONS = EXPLANATIONS;
